import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { STATUS_STYLES } from '../constants';
import toast from 'react-hot-toast';

const { FiUpload, FiDollarSign, FiTrendingUp, FiTrendingDown, FiAlertCircle, FiPackage, FiRefreshCw } = FiIcons;

const StockPricing = () => {
  const [stockItems, setStockItems] = useState([
    {
      sku: 'APL-001',
      name: 'Granny Smith Apples',
      currentStock: 450,
      unitCost: 12.00,
      unitSellPrice: 15.50,
      margin: 29.2,
      lastUpdated: '2024-01-10',
      hasPendingUpdate: false
    },
    {
      sku: 'ORG-023',
      name: 'Navel Oranges',
      currentStock: 180,
      unitCost: 14.50,
      unitSellPrice: 18.00,
      margin: 24.1,
      lastUpdated: '2024-01-10',
      hasPendingUpdate: false
    },
    {
      sku: 'BAN-012',
      name: 'Cavendish Bananas',
      currentStock: 75,
      unitCost: 9.00,
      unitSellPrice: 12.00,
      margin: 33.3,
      lastUpdated: '2024-01-10',
      hasPendingUpdate: false
    },
    {
      sku: 'TOM-045',
      name: 'Beef Tomatoes',
      currentStock: 320,
      unitCost: 18.00,
      unitSellPrice: 25.00,
      margin: 38.9,
      lastUpdated: '2024-01-10',
      hasPendingUpdate: false
    }
  ]);

  const [priceHistory, setPriceHistory] = useState({});
  const [importing, setImporting] = useState(false);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setImporting(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

        // Expected format: SKU, Name, Cost Price, Sell Price
        const skuIndex = headers.indexOf('sku');
        const costIndex = headers.findIndex(h => h.includes('cost'));
        const sellIndex = headers.findIndex(h => h.includes('sell'));

        if (skuIndex === -1 || costIndex === -1 || sellIndex === -1) {
          toast.error('CSV must contain SKU, Cost Price, and Sell Price columns');
          setImporting(false);
          return;
        }

        const updates = {};
        const newHistory = { ...priceHistory };

        // Process each line
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          const sku = values[skuIndex];
          const newCost = parseFloat(values[costIndex]);
          const newSell = parseFloat(values[sellIndex]);

          if (sku && !isNaN(newCost) && !isNaN(newSell)) {
            updates[sku] = { newCost, newSell };
          }
        }

        // Update stock items with new prices
        setStockItems(prevItems =>
          prevItems.map(item => {
            if (updates[item.sku]) {
              const { newCost, newSell } = updates[item.sku];
              const newMargin = ((newSell - newCost) / newSell * 100).toFixed(1);

              // Store price history
              if (!newHistory[item.sku]) {
                newHistory[item.sku] = [];
              }
              newHistory[item.sku].push({
                oldCost: item.unitCost,
                oldSell: item.unitSellPrice,
                oldMargin: item.margin,
                effectiveDate: item.lastUpdated,
                stockAtOldPrice: item.currentStock
              });

              return {
                ...item,
                unitCost: newCost,
                unitSellPrice: newSell,
                margin: parseFloat(newMargin),
                lastUpdated: new Date().toISOString().split('T')[0],
                hasPendingUpdate: true
              };
            }
            return item;
          })
        );

        setPriceHistory(newHistory);
        toast.success(`Price updates imported for ${Object.keys(updates).length} items`);
        setImporting(false);

      } catch (error) {
        console.error('Error parsing CSV:', error);
        toast.error('Error parsing CSV file');
        setImporting(false);
      }
    };

    reader.onerror = () => {
      toast.error('Error reading file');
      setImporting(false);
    };

    reader.readAsText(file);
  };

  const calculatePriceChange = (oldPrice, newPrice) => {
    const change = ((newPrice - oldPrice) / oldPrice * 100).toFixed(1);
    return change;
  };

  const metrics = {
    totalItems: stockItems.length,
    itemsWithUpdates: stockItems.filter(item => item.hasPendingUpdate).length,
    avgMargin: (stockItems.reduce((sum, item) => sum + item.margin, 0) / stockItems.length).toFixed(1),
    totalStockValue: stockItems.reduce((sum, item) => sum + (item.currentStock * item.unitCost), 0)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-[#e8f5e9]">
            Stock & <span className="text-[#84cc16]">Pricing</span> Management
          </h1>
          <p className="text-[#6b8772] font-mono text-sm mt-1">Manage inventory pricing and supplier updates</p>
        </div>
        <div className="flex items-center space-x-3">
          <label
            htmlFor="csv-upload"
            className="bg-gradient-to-r from-lime-600 to-lime-500 text-white px-4 py-2 rounded-lg hover:from-lime-500 hover:to-lime-400 transition-all flex items-center space-x-2 cursor-pointer shadow-lg shadow-lime-500/20"
          >
            <SafeIcon icon={importing ? FiRefreshCw : FiUpload} className={`w-4 h-4 ${importing ? 'animate-spin' : ''}`} />
            <span>{importing ? 'Importing...' : 'Import Supplier CSV'}</span>
          </label>
          <input
            id="csv-upload"
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
            disabled={importing}
          />
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#2d3f35] transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Total Items</p>
              <p className="text-2xl font-bold text-sky-400 mt-1">{metrics.totalItems}</p>
            </div>
            <div className="bg-sky-500/10 rounded-lg p-3 border border-sky-500/30">
              <SafeIcon icon={FiPackage} className="w-6 h-6 text-sky-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#2d3f35] transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Pending Updates</p>
              <p className="text-2xl font-bold text-amber-400 mt-1">{metrics.itemsWithUpdates}</p>
            </div>
            <div className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/30">
              <SafeIcon icon={FiAlertCircle} className="w-6 h-6 text-amber-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#2d3f35] transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Avg Margin</p>
              <p className="text-2xl font-bold text-lime-400 mt-1">{metrics.avgMargin}%</p>
            </div>
            <div className="bg-lime-500/10 rounded-lg p-3 border border-lime-500/30">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-lime-400" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-[#111916] rounded-xl p-5 border border-[#243129] hover:border-[#2d3f35] transition-colors"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Stock Value</p>
              <p className="text-2xl font-bold text-violet-400 mt-1">N$ {(metrics.totalStockValue / 1000).toFixed(0)}k</p>
            </div>
            <div className="bg-violet-500/10 rounded-lg p-3 border border-violet-500/30">
              <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-violet-400" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Info Banner */}
      {metrics.itemsWithUpdates > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4"
        >
          <div className="flex items-start space-x-3">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-amber-400 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-amber-300">Price Updates Imported</h4>
              <p className="text-sm text-amber-400/80 mt-1">
                {metrics.itemsWithUpdates} items have new pricing. Existing stock will be sold at old prices.
                New stock received will use the updated pricing.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Stock Items Table */}
      <div className="bg-[#111916] rounded-xl border border-[#243129] overflow-hidden">
        <div className="p-6 border-b border-[#1c2822]">
          <h3 className="text-lg font-semibold text-[#e8f5e9]">Current Stock & Pricing</h3>
          <p className="text-[#6b8772] font-mono text-sm mt-1">View and manage item pricing with history</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#0a0f0d] border-b border-[#1c2822]">
              <tr>
                <th className="px-6 py-3 text-left text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">SKU / Product</th>
                <th className="px-6 py-3 text-left text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Stock</th>
                <th className="px-6 py-3 text-left text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Cost Price</th>
                <th className="px-6 py-3 text-left text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Sell Price</th>
                <th className="px-6 py-3 text-left text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Margin</th>
                <th className="px-6 py-3 text-left text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-[10px] font-mono font-medium text-[#6b8772] uppercase tracking-wider">Last Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1c2822]">
              {stockItems.map((item, index) => {
                const history = priceHistory[item.sku];
                const hasHistory = history && history.length > 0;
                const latestHistory = hasHistory ? history[history.length - 1] : null;

                return (
                  <motion.tr
                    key={item.sku}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-[#1a2420] transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-[#e8f5e9]">{item.name}</div>
                        <div className="text-sm text-[#6b8772] font-mono">{item.sku}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-[#e8f5e9]">{item.currentStock} units</div>
                      {hasHistory && (
                        <div className="text-xs text-amber-400">
                          {latestHistory.stockAtOldPrice} @ old price
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-[#e8f5e9]">N$ {item.unitCost.toFixed(2)}</div>
                      {hasHistory && (
                        <div className="flex items-center text-xs text-[#6b8772]">
                          <span>Was N$ {latestHistory.oldCost.toFixed(2)}</span>
                          <SafeIcon
                            icon={item.unitCost > latestHistory.oldCost ? FiTrendingUp : FiTrendingDown}
                            className={`w-3 h-3 ml-1 ${item.unitCost > latestHistory.oldCost ? 'text-red-400' : 'text-lime-400'}`}
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-[#e8f5e9]">N$ {item.unitSellPrice.toFixed(2)}</div>
                      {hasHistory && (
                        <div className="flex items-center text-xs text-[#6b8772]">
                          <span>Was N$ {latestHistory.oldSell.toFixed(2)}</span>
                          <SafeIcon
                            icon={item.unitSellPrice > latestHistory.oldSell ? FiTrendingUp : FiTrendingDown}
                            className={`w-3 h-3 ml-1 ${item.unitSellPrice > latestHistory.oldSell ? 'text-lime-400' : 'text-red-400'}`}
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-lime-400">{item.margin}%</div>
                      {hasHistory && (
                        <div className="text-xs text-[#6b8772]">
                          Was {latestHistory.oldMargin}%
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {item.hasPendingUpdate ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/30">
                          Updated
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#6b8772]/10 text-[#6b8772] border border-[#6b8772]/30">
                          Current
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[#6b8772]">
                      {item.lastUpdated}
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* CSV Format Help */}
      <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-sky-400 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-sky-300">CSV Format Instructions</h4>
            <p className="text-sm text-sky-400/80 mt-1">
              Your CSV file should include columns: <strong className="text-sky-300">SKU</strong>, <strong className="text-sky-300">Name</strong>, <strong className="text-sky-300">Cost Price</strong>, and <strong className="text-sky-300">Sell Price</strong>.
            </p>
            <div className="mt-2 text-xs text-sky-400 bg-sky-500/10 rounded px-2 py-1 font-mono border border-sky-500/20">
              SKU, Name, Cost Price, Sell Price<br />
              APL-001, Granny Smith Apples, 13.50, 17.00
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockPricing;
